import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X,
  Users,
  UserPlus,
  Filter,
  Download,
  Upload,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import type { EmpresaListItem } from '../../../types';
import { useUsuariosEmpresa, useEstadisticasUsuarios } from '../../../hooks/useUsuariosEmpresa';
import UsuariosEmpresaTable from './UsuariosEmpresaTable';
import UserSearchModal from './UserSearchModal';
import PermissionsMatrix from './PermissionsMatrix';

interface UsuariosEmpresaModalProps {
  empresa: EmpresaListItem;
  isOpen: boolean;
  onClose: () => void;
}

const UsuariosEmpresaModal: React.FC<UsuariosEmpresaModalProps> = ({
  empresa,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'usuarios' | 'buscar' | 'permisos'>('usuarios');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'inactivos'>('activos');

  // Hooks para datos y estadísticas
  const { 
    data: usuarios, 
    isLoading, 
    error, 
    refetch 
  } = useUsuariosEmpresa(empresa.id);
  
  const { estadisticas } = useEstadisticasUsuarios(empresa.id);

  if (!isOpen) return null;

  // Filtrar usuarios según tab activo
  const usuariosFiltrados = usuarios?.filter(usuario => {
    switch (filtroActivo) {
      case 'activos':
        return usuario.es_activa;
      case 'inactivos':
        return !usuario.es_activa;
      default:
        return true;
    }
  }) || [];

  const handleClose = () => {
    setSelectedUsers([]);
    setActiveTab('usuarios');
    onClose();
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleAsignarNuevo = () => {
    setShowSearchModal(true);
  };

  const handleExportarUsuarios = () => {
    // TODO: Implementar exportación de usuarios
    console.log('Exportar usuarios de empresa:', empresa.id);
  };

  const handleAsignacionMasiva = () => {
    // TODO: Implementar asignación masiva
    console.log('Asignación masiva para empresa:', empresa.id);
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        {/* Modal Container */}
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-green-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('admin.title')}
                </h2>
                <p className="text-sm text-gray-600">
                  {empresa.nombre} • {empresa.rut}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Estadísticas Rápidas */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{estadisticas.total}</p>
                  <p className="text-xs text-gray-600">{t('common.all')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{estadisticas.activos}</p>
                  <p className="text-xs text-gray-600">{t('admin.status_active')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{estadisticas.por_rol.administrador}</p>
                  <p className="text-xs text-gray-600">{t('common.status')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Info className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{estadisticas.empresas_principales}</p>
                  <p className="text-xs text-gray-600">{t('admin.companies')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="px-6 py-3 border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('usuarios')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'usuarios'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('admin.title')}
              </button>
              
              <button
                onClick={() => setActiveTab('permisos')}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'permisos'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Matrix de Permisos
              </button>
            </nav>
          </div>

          {/* Actions Bar */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Filtros */}
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <select
                    value={filtroActivo}
                    onChange={(e) => setFiltroActivo(e.target.value as any)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="todos">{t('common.all')}</option>
                    <option value="activos">{t('admin.status_active')}</option>
                    <option value="inactivos">{t('admin.status_inactive')}</option>
                  </select>
                </div>
                
                {selectedUsers.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {selectedUsers.length} seleccionados
                    </span>
                    <button
                      onClick={() => setSelectedUsers([])}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('common.clear_filters')}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {/* Refresh */}
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                  title="Actualizar"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                
                {/* Export */}
                <button
                  onClick={handleExportarUsuarios}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Exportar usuarios"
                >
                  <Download className="h-4 w-4" />
                </button>
                
                {/* Asignación masiva */}
                <button
                  onClick={handleAsignacionMasiva}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Asignación masiva"
                >
                  <Upload className="h-4 w-4" />
                </button>
                
                {/* Asignar nuevo usuario */}
                <button
                  onClick={handleAsignarNuevo}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>{t('admin.add')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {error && (
              <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-700">
                      {t('errors.load_error')}: {error.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'usuarios' && (
              <UsuariosEmpresaTable
                empresa={empresa}
                usuarios={usuariosFiltrados}
                selectedUsers={selectedUsers}
                onSelectedUsersChange={setSelectedUsers}
                loading={isLoading}
              />
            )}

            {activeTab === 'permisos' && (
              <PermissionsMatrix
                empresa={empresa}
                usuarios={usuariosFiltrados}
                loading={isLoading}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {usuariosFiltrados.length} de {estadisticas.total} usuarios
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de búsqueda de usuarios */}
      {showSearchModal && (
        <>
          <UserSearchModal
            empresa={empresa}
            isOpen={showSearchModal}
            onClose={() => setShowSearchModal(false)}
            onUserAssigned={() => {
              setShowSearchModal(false);
              refetch();
            }}
          />
        </>
      )}
    </>
  );
};

export default UsuariosEmpresaModal;
